/* La file d'attente des écritures.
 *
 * ── Le problème, tel qu'il se pose sur un parcours ──────────────────────────
 *
 * Un golf est un endroit sans réseau. Pas « avec un réseau lent » : un creux
 * derrière une butte, quatre trous sans rien, puis trois barres au club-house.
 * Jusqu'ici chaque coup partait au serveur et l'écran attendait sa réponse —
 * le joueur voyait donc une roue tourner au moment précis où il voulait ranger
 * son téléphone et jouer le coup suivant. Et si la réponse ne venait pas, le
 * coup était perdu.
 *
 * ── Le principe : écrire d'abord, porter ensuite ────────────────────────────
 *
 * Une écriture n'attend plus rien. Elle est rangée ici, dans l'ordre, et la
 * saisie continue. Une boucle de fond la porte au serveur dès qu'il répond.
 * L'écran ne dépend jamais du réseau ; l'indicateur dit ce qui reste à porter.
 *
 * ── Ce qui rend le rejeu SÛR ────────────────────────────────────────────────
 *
 * Rejouer une écriture ne doit pas la compter deux fois. Trois mécanismes, un
 * par famille :
 *
 *   * les coups d'un parcours sont adressés par CLÉ NATURELLE — tour, joueur,
 *     trou, rang du coup — et le serveur en fait un « ON CONFLICT DO UPDATE » :
 *     rejouer récrit la même ligne ;
 *   * les balles du practice portent un jeton que le serveur reconnaît, et
 *     auquel il répond sans rien insérer ;
 *   * les créations — un tour, une séance, une série — portent l'identifiant
 *     tiré ICI. Le serveur l'accepte tel quel et ignore une seconde création
 *     du même. C'est aussi ce qui permet aux écritures suivantes de s'y
 *     rattacher sans attendre de réponse.
 *
 * ── Ce qu'on fait d'une écriture refusée ────────────────────────────────────
 *
 * Une panne de réseau se retente indéfiniment : rien n'est perdu, on attend.
 * Un refus du serveur, lui, ne s'arrangera pas en le répétant — et une file
 * bloquée sur une écriture morte cesserait de porter TOUTES les suivantes.
 * Elle est donc retirée et comptée à part, pour être dite au joueur.
 *
 * Deux exceptions traitées comme des pannes et non comme des refus : 401 et
 * 403. Une session expirée revient ; effacer la carte d'un joueur parce que
 * son jeton a tourné pendant qu'il jouait serait le pire des dénouements.
 */

import { ApiError, api } from './api.ts';
import { MAGASIN_FILE, ecrire, effacer, identifiant, toutLire } from './local.ts';

export interface Ecriture {
    /** Clé auto-incrémentée : l'ordre de rejeu. Absente avant l'insertion. */
    rang?: number;
    /** Identifiant d'idempotence, et identifiant de l'objet créé le cas échéant. */
    id: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    body?: unknown;
    /** Ce que l'écriture veut dire, en français, pour le journal des pertes. */
    quoi: string;
    date: number;
    /** L'objet que cette écriture CRÉE, s'il y en a un. C'est par là que se
     *  trouve tout ce qui en dépend : les balles d'une série citent la série
     *  dans leur chemin, les trous d'un tour citent le tour. */
    cree?: string;
}

export interface EtatFile {
    /** Le réseau répond-il ? Mesuré sur les vraies requêtes, et pas seulement
     *  sur `navigator.onLine` — qui affirme « en ligne » sur un portail
     *  captif, et sur un wifi de club-house qui ne route rien. */
    enLigne: boolean;
    /** Écritures encore à porter. */
    enAttente: number;
    /** Une vidange est en cours. */
    envoiEnCours: boolean;
    /** Écritures refusées par le serveur, et donc abandonnées. */
    perdues: { quoi: string; raison: string }[];
}

let etat: EtatFile = {
    enLigne: typeof navigator === 'undefined' ? true : navigator.onLine,
    enAttente: 0,
    envoiEnCours: false,
    perdues: [],
};

const abonnes = new Set<(e: EtatFile) => void>();

function publier(patch: Partial<EtatFile>): void {
    etat = { ...etat, ...patch };
    for (const cb of abonnes) cb(etat);
}

export function abonner(cb: (e: EtatFile) => void): () => void {
    abonnes.add(cb);
    cb(etat);
    return () => { abonnes.delete(cb); };
}

export const etatFile = (): EtatFile => etat;

/** Recompte ce qui reste, depuis la base : la source de vérité est le disque. */
async function recompter(): Promise<number> {
    const n = (await toutLire<Ecriture>(MAGASIN_FILE)).length;
    publier({ enAttente: n });
    return n;
}

/** Range une écriture et lance une vidange, sans l'attendre.
 *
 *  Rend l'identifiant de l'écriture — celui sous lequel l'objet créé sera
 *  connu du serveur, de sorte que l'appelant peut l'utiliser tout de suite. */
export async function enfiler(
    e: Omit<Ecriture, 'rang' | 'date' | 'id'> & { id?: string },
): Promise<string> {
    const id = e.id ?? identifiant();
    await ecrire(MAGASIN_FILE, { ...e, id, date: Date.now() });
    await recompter();
    void vidanger();
    return id;
}

/** Ce qu'on fait d'une écriture selon la réponse du serveur.
 *
 *  Isolée et exportée parce que c'est LA décision délicate de la file : se
 *  tromper d'un côté bloque tout ce qui suit derrière une écriture morte, se
 *  tromper de l'autre efface une carte pour une panne passagère. */
export function verdict(status: number): 'attendre' | 'abandonner' {
    // 401/403 : la session a expiré ou tourné pendant qu'on jouait. Elle
    // revient dès la reconnexion, et l'écriture avec elle.
    if (status === 401 || status === 403) return 'attendre';
    // 408/429 : le serveur dit lui-même de réessayer.
    if (status === 408 || status === 429) return 'attendre';
    // 5xx : une panne d'en face, pas une écriture fautive.
    if (status >= 500) return 'attendre';
    // Le reste — 400, 404, 409, 422 — ne s'arrangera pas en le répétant.
    return 'abandonner';
}

let vidangeEnCours: Promise<void> | null = null;

/** Porte au serveur tout ce qui attend, dans l'ordre, une écriture à la fois.
 *
 *  UNE à la fois, et non en parallèle : deux balles d'une même série envoyées
 *  ensemble arriveraient dans un ordre indécis, et le rang d'un coup fait
 *  partie de son sens. La lenteur est sans conséquence — personne n'attend. */
export function vidanger(): Promise<void> {
    vidangeEnCours ??= (async () => {
        try {
            publier({ envoiEnCours: true });
            for (;;) {
                const toutes = await toutLire<Ecriture>(MAGASIN_FILE);
                if (toutes.length === 0) { publier({ enAttente: 0 }); return; }
                toutes.sort((a, b) => (a.rang ?? 0) - (b.rang ?? 0));
                const e = toutes[0]!;
                try {
                    await api(e.path, { method: e.method, body: e.body });
                } catch (err) {
                    if (err instanceof ApiError) {
                        if (verdict(err.status) === 'attendre') {
                            // Le serveur a RÉPONDU : il est joignable, même
                            // quand il dit non. Sauf 5xx, où il est debout mais
                            // hors service — ce que l'indicateur doit dire.
                            publier({ enLigne: err.status < 500 });
                            return;
                        }
                        // Un refus franc ne s'arrangera pas : on le retire pour
                        // que la file continue de porter les suivantes, et on
                        // le garde de côté pour le dire.
                        await effacer(MAGASIN_FILE, e.rang!);
                        // Et ce qui en dépendait part avec, en UNE ligne : sans
                        // cela, une série refusée faisait refuser ses cinq
                        // balles ensuite, chacune sur sa propre ligne, sans que
                        // la cause commune soit dite nulle part.
                        const emportees = e.cree ? await purgerDependants(e.cree) : [];
                        publier({
                            enLigne: true,
                            perdues: [...etat.perdues, {
                                quoi: emportees.length > 0
                                    ? `${e.quoi} (et ${emportees.length} écriture${emportees.length > 1 ? 's' : ''} qui en dépendai${emportees.length > 1 ? 'ent' : 't'})`
                                    : e.quoi,
                                raison: err.message,
                            }],
                        });
                        await recompter();
                        continue;
                    }
                    // `fetch` qui échoue : pas de réseau. On garde tout.
                    publier({ enLigne: false });
                    return;
                }
                await effacer(MAGASIN_FILE, e.rang!);
                publier({ enLigne: true });
                await recompter();
            }
        } finally {
            publier({ envoiEnCours: false });
            vidangeEnCours = null;
        }
    })();
    return vidangeEnCours;
}

/* Jeter une écriture, et tout ce qui n'a plus de sens sans elle.
 *
 * ── Pourquoi la récursion ───────────────────────────────────────────────────
 *
 * Les écritures se tiennent : les balles s'adressent à une série, la série à
 * une séance, les trous à un tour. Jeter la séance sans jeter le reste laissait
 * dix-huit écritures orphelines partir une par une vers un objet inexistant, et
 * se faire refuser une par une — dix-huit lignes dans le bandeau pour une seule
 * cause.
 *
 * On suit donc le lien dans les deux sens : une écriture VISE un objet quand
 * son chemin le cite, et elle en CRÉE parfois un autre, qu'on suit à son tour.
 *
 * ── Les deux moments où l'on s'en sert ──────────────────────────────────────
 *
 *   * le joueur abandonne — la séance est effacée en base, ce qui attendait
 *     n'a plus d'objet ;
 *   * le serveur refuse une création — un club retiré du sac, par exemple —
 *     et ce qu'elle portait ne peut plus atterrir nulle part.
 */
export async function purgerDependants(id: string): Promise<Ecriture[]> {
    const jetees: Ecriture[] = [];
    const aSuivre = [id];
    const vues = new Set<string>();
    while (aSuivre.length > 0) {
        const cible = aSuivre.pop()!;
        if (vues.has(cible)) continue;
        vues.add(cible);
        for (const e of await toutLire<Ecriture>(MAGASIN_FILE)) {
            if (e.path.includes(cible) || e.cree === cible) {
                await effacer(MAGASIN_FILE, e.rang!);
                jetees.push(e);
                if (e.cree) aSuivre.push(e.cree);
            }
        }
    }
    if (jetees.length > 0) await recompter();
    return jetees;
}

/* Une LECTURE qui échoue en dit autant qu'une écriture.
 *
 * L'indicateur ne se réveillait qu'à la vidange, donc jamais tant que la file
 * était vide : un serveur injoignable au premier écran de la journée passait
 * inaperçu. Les lectures qui savent se replier sur une copie locale le disent
 * désormais ici.
 *
 * `navigator.onLine` ne suffit pas à le savoir : le téléphone est en ligne, le
 * serveur ne répond pas — un wifi de club-house, un tunnel, une panne. */
export function signalerReseau(joignable: boolean): void {
    if (etat.enLigne !== joignable) publier({ enLigne: joignable });
    if (joignable && etat.enAttente > 0) void vidanger();
}

/** Efface le journal des pertes, une fois qu'il a été montré. */
export function oublierPertes(): void {
    publier({ perdues: [] });
}

/** Y a-t-il des écritures en attente touchant ce chemin ?
 *
 *  Sert aux écrans qui rechargent depuis le serveur : tant qu'une écriture
 *  n'est pas portée, la réponse du serveur est EN RETARD sur l'écran, et
 *  l'écraser ferait clignoter un score qu'on vient de saisir. */
export async function enAttenteSur(prefixe: string): Promise<boolean> {
    const toutes = await toutLire<Ecriture>(MAGASIN_FILE);
    return toutes.some((e) => e.path.startsWith(prefixe));
}

let demarree = false;

/** Branche la file sur la vie de l'application. Appelé une fois, au démarrage. */
export function demarrerFile(): void {
    if (demarree) return;
    demarree = true;
    void recompter().then(() => vidanger());

    window.addEventListener('online', () => { publier({ enLigne: true }); void vidanger(); });
    window.addEventListener('offline', () => publier({ enLigne: false }));
    // Le retour sur l'application est le bon moment : on ressort son téléphone
    // au trou suivant, souvent quelques centaines de mètres plus loin — parfois
    // sous une antenne.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void vidanger();
    });
    // Et régulièrement, pour le cas où le réseau revient sans que le navigateur
    // le dise : « online » ne se déclenche pas quand on repasse d'un portail
    // captif à une vraie connexion.
    setInterval(() => { void vidanger(); }, 20_000);
}
